import type {
  AccountDeletionResult,
  ApiErrorBody,
  ArchiveDTO,
  BillingConfigDTO,
  ConsentStatusDTO,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
  GroupItemsResponse,
  ListGroupsResponse,
  ListItemsQuery,
  ListItemsResponse,
  UpdateArchiveSettingsRequest,
  UpdateConsentRequest,
} from "@barkup/shared";
import { getIdToken } from "./liff.js";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    body: ApiErrorBody
  ) {
    super(body.message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${getIdToken()}`,
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: "unknown", message: res.statusText }))) as ApiErrorBody;
    throw new ApiError(res.status, body);
  }
  return res.json() as Promise<T>;
}

export function resolveArchive(params: { ownerType: "USER" | "GROUP"; lineOwnerId?: string }): Promise<ArchiveDTO> {
  const qs = new URLSearchParams({ ownerType: params.ownerType, ...(params.lineOwnerId ? { lineOwnerId: params.lineOwnerId } : {}) });
  return request<ArchiveDTO>(`/api/archives/resolve?${qs.toString()}`);
}

/**
 * The GROUP archives the caller currently belongs to (DESIGN.md §5) — powers
 * GroupManagementView's "manage each group's files" list. Membership is
 * verified server-side per group, so this only ever returns groups the caller
 * may actually open.
 */
export function listGroups(): Promise<ArchiveDTO[]> {
  return request<ListGroupsResponse>(`/api/archives`).then((r) => r.groups);
}

export function updateArchiveSettings(archiveId: string, body: UpdateArchiveSettingsRequest): Promise<ArchiveDTO> {
  return request<ArchiveDTO>(`/api/archives/${archiveId}/settings`, { method: "PATCH", body: JSON.stringify(body) });
}

/**
 * Right-to-object state (DESIGN.md §7.1). Person-level, not archive-level —
 * the same flag the "ถอนความยินยอม" chat command sets, so the two paths can
 * never disagree about whether someone has objected.
 */
export function getConsent(): Promise<ConsentStatusDTO> {
  return request<ConsentStatusDTO>(`/api/consent`);
}

export function setConsent(optedOut: boolean): Promise<ConsentStatusDTO> {
  const body: UpdateConsentRequest = { optedOut };
  return request<ConsentStatusDTO>(`/api/consent`, { method: "POST", body: JSON.stringify(body) });
}

/** Which purchase paths this deploy can complete — drives what PlansView offers. */
export function getBillingConfig(): Promise<BillingConfigDTO> {
  return request<BillingConfigDTO>(`/api/billing/config`);
}

/**
 * Starts a hosted checkout and returns the URL to send the user to. Creating
 * the session grants nothing — fulfilment happens only when Stripe's webhook
 * confirms payment, which for PromptPay is minutes after the QR is shown.
 */
export function createCheckout(body: CreateCheckoutRequest): Promise<string> {
  return request<CreateCheckoutResponse>(`/api/billing/checkout`, {
    method: "POST",
    body: JSON.stringify(body),
  }).then((r) => r.checkoutUrl);
}

/**
 * `signal` lets a superseded search be abandoned mid-flight. Without it, typing
 * "report" fired six overlapping requests whose responses could land in any
 * order, so a slow early one could overwrite the results of a later, more
 * specific query — the list would settle on the wrong answer with no way to tell.
 */
export function listItems(query: ListItemsQuery, signal?: AbortSignal): Promise<ListItemsResponse> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) qs.set(key, String(value));
  }
  return request<ListItemsResponse>(`/api/items?${qs.toString()}`, { signal });
}

/** True for a fetch abandoned via `signal` — a superseded request, not a failure to surface. */
export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

export function deleteItems(itemIds: string[]): Promise<{ deleted: number }> {
  return request(`/api/items/delete`, { method: "POST", body: JSON.stringify({ itemIds }) });
}

export function restoreItems(itemIds: string[]): Promise<{ restored: number }> {
  return request(`/api/items/restore`, { method: "POST", body: JSON.stringify({ itemIds }) });
}

/** Notice-and-takedown (DESIGN.md §9.2): hides the item for everyone immediately, pending operator review. */
export function reportItem(itemId: string, reason: string): Promise<{ reported: boolean }> {
  return request(`/api/items/report`, { method: "POST", body: JSON.stringify({ itemId, reason }) });
}

/** Every item the caller has ever sent into a GROUP archive — powers the §7.1 "force-view" gate before account deletion. */
export function getMyGroupItems(): Promise<GroupItemsResponse> {
  return request<GroupItemsResponse>(`/api/account/group-items`);
}

/** Right-to-erasure (DESIGN.md §7.1): deletes the caller's own 1:1 archive and anonymizes their name off everything else. Irreversible. */
export function deleteAccount(): Promise<AccountDeletionResult> {
  return request<AccountDeletionResult>(`/api/account/delete`, { method: "POST" });
}

/**
 * Fetches an item's decrypted bytes as an object URL.
 *
 * Can't be a plain `<img src>` or `<a href>`: the endpoint authenticates with
 * the LIFF ID token as a Bearer header, and you cannot set headers on an
 * element's src/href — so the bytes have to come through `fetch` and become a
 * blob URL.
 *
 * Cached per item: the same URL backs both the thumbnail and the download, so
 * viewing an image then saving it doesn't decrypt and transfer it twice.
 * Callers must not revoke these — releaseObjectUrls() owns their lifetime.
 */
const objectUrlCache = new Map<string, Promise<string>>();

function fetchObjectUrl(cacheKey: string, path: string): Promise<string> {
  const cached = objectUrlCache.get(cacheKey);
  if (cached) return cached;

  const pending = (async () => {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${getIdToken()}` },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({ error: "unknown", message: res.statusText }))) as ApiErrorBody;
      throw new ApiError(res.status, body);
    }
    return URL.createObjectURL(await res.blob());
  })();

  // Don't cache a rejection — a transient failure would otherwise poison the
  // item for the rest of the session.
  pending.catch(() => objectUrlCache.delete(cacheKey));
  objectUrlCache.set(cacheKey, pending);
  return pending;
}

export function fetchItemObjectUrl(itemId: string): Promise<string> {
  return fetchObjectUrl(itemId, `/api/items/${itemId}/download`);
}

/**
 * Server-generated ~512px WebP preview — tens of KB instead of the full
 * original. Only call when the item's `hasThumb` is true; 404s otherwise.
 */
export function fetchItemThumbUrl(itemId: string): Promise<string> {
  return fetchObjectUrl(`thumb:${itemId}`, `/api/items/${itemId}/thumbnail`);
}

/** Frees every cached blob URL. Call on unmount so images don't leak. */
export function releaseObjectUrls(): void {
  for (const pending of objectUrlCache.values()) {
    pending.then(URL.revokeObjectURL).catch(() => undefined);
  }
  objectUrlCache.clear();
}

/** iPhone/iPad — incl. iPadOS 13+, which reports as "MacIntel" but is a touch device. */
function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

/**
 * Hand the file to the OS share sheet as a File. On iOS this is the only route
 * that actually saves: an image offers "Save Image" (→ Photos library), other
 * types offer "Save to Files". Returns true when it handled the save (including
 * a user cancel, so the caller doesn't then fire a redundant <a download>),
 * false when sharing a file isn't supported and the anchor fallback should run.
 */
async function shareFile(objectUrl: string, filename: string | null, mimeType: string | null): Promise<boolean> {
  if (typeof navigator.share !== "function" || typeof navigator.canShare !== "function") return false;
  try {
    const blob = await fetch(objectUrl).then((r) => r.blob());
    const type = mimeType || blob.type || "application/octet-stream";
    const name = filename ?? `file.${type.split("/")[1] || "bin"}`;
    const file = new File([blob], name, { type });
    if (!navigator.canShare({ files: [file] })) return false;
    await navigator.share({ files: [file] });
    return true;
  } catch (err) {
    // User dismissed the share sheet — treat as handled (don't double up with a
    // download). Any other failure falls through to the anchor path.
    return err instanceof DOMException && err.name === "AbortError";
  }
}

/** Downloads + decrypts an item and saves it to the device. */
export async function downloadItem(
  itemId: string,
  filename: string | null,
  mimeType: string | null = null
): Promise<void> {
  const url = await fetchItemObjectUrl(itemId);

  // iOS webviews (incl. LINE's LIFF browser) silently ignore a blob <a download>
  // — nothing saves. The share sheet is the working path: an image lands in the
  // Photos library ("Save Image"), anything else in Files. Desktop/Android keep
  // the direct anchor download below.
  if (isIOS() && (await shareFile(url, filename, mimeType))) return;

  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? itemId;
  // Must be in the document for the click to be honoured in some mobile
  // webviews, and the URL must NOT be revoked synchronously afterwards — doing
  // that races the browser, which may not have started reading the blob yet,
  // and the download silently does nothing. The cache owns the URL instead.
  document.body.appendChild(a);
  a.click();
  a.remove();
}
